/**
 * OutlineEditController — the point-editing drags for a path-backed
 * entity that is NOT a construction line (#969, epic #781). Piece
 * outlines compose it first; cutouts and stitch polylines follow in
 * #970, and #971's rotate/resize reuses the same `OutlineTarget`
 * abstraction.
 *
 * Deliberately lean compared to the construction VertexEditController:
 * no mirror routing, no snap-chain closures, no coincident
 * constraints, no primitive metadata — an outline is one path and the
 * three drags (anchor / bezier handle / curve bend) mutate it
 * directly. The load-bearing math is shared via `outlineEditGeometry`
 * so the two controllers can never drift apart geometrically.
 */
import type paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import type { ConstructionLine } from "../model/Project";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isGridSnapActive,
  snapPointToGrid,
  snapToCommonAngle,
} from "./dragSnap";
import { setAngleHint } from "../snapAngleIndicator";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  applyCurveBend,
  findCurveAt,
  findSegmentAt,
  mirroredHandleFor,
  resolveSubpaths,
  type CurveBendSnapshot,
  type OutlineAnchorRef,
} from "./outlineEditGeometry";
import {
  currentPointerKind,
  dotHitRadiusMm,
  hitRadiusFor,
} from "../canvas/pointerPrecision";

/** A hit on one of the outline's visible edit handles. */
export type OutlineHandleHit =
  | ({ kind: "anchor" } & OutlineAnchorRef)
  | ({ kind: "bezier-tip"; side: "in" | "out" } & OutlineAnchorRef);

/** A hit on the outline's stroke. */
export interface OutlineCurveHit {
  pathIndex: number;
  curveIndex: number;
  t: number;
}

/** A path-backed editable entity. The controller only ever talks to
 *  the outline through this interface, so #970 can plug cutouts
 *  (closed) and stitch polylines (open) in without touching the drag
 *  logic. */
export interface OutlineTarget {
  /** Stable identity, e.g. "piece:<id>". A key change mid-drag
   *  aborts the drag (the entity was swapped or deleted). */
  key: string;
  /** Pieces / cutouts: true. Stitch polylines (#970): false. */
  mustStayClosed: boolean;
  /** Minimum anchors a delete may leave: 3 closed, 2 open. */
  minAnchors: number;
  /** Whether the outline may carry bezier curves. True everywhere since seams
   *  gained curve geometry (#982); it was false for stitch polylines under
   *  #970, which is why curve bends, tangent drags and the smooth toggle used
   *  to be unavailable there. */
  allowCurves: boolean;
  /** Human noun for undo labels: "part outline" / "cutout" / "seam". */
  noun: string;
  /** Model-backed anchor lock lookup (piece / cutout / seam
   *  `smoothAnchors`, #969 parity) — the ONLY source of truth for whether an
   *  anchor is locked, and `null` means genuinely unlocked.
   *
   *  It used to be optional, with the controller falling back to reading the
   *  lock out of the handle geometry when it returned `null`. That fallback
   *  could not tell "no lock recorded" from "deliberately unlocked", and the
   *  bezier cycle's independent-handles state is exactly the second: it
   *  bootstraps two mirrored handles and records no entry, which the geometric
   *  read then called "smooth". So the first click on a sharp corner produced
   *  handles that moved together, three states early (#1099). Construction has
   *  never inferred — `smoothAnchors` is its truth, seeded at creation — and
   *  this is now the same rule. */
  lockModeAt(ref: OutlineAnchorRef): "angle" | "smooth" | null;
  /** Committed pathData (model truth) — used by tap actions. Given in the
   *  same DISPLAYED space as the live item and the cursor: a part moved in the
   *  Spread view is drawn shifted from its model (#1140), and the target maps
   *  back on commit. */
  pathData(): string;
  /** The live rendered paper item the drags mutate per-frame; null
   *  aborts the drag (the Scene re-rendered it away). */
  liveItem(): paper.PathItem | null;
  /** Re-paint the anchor/handle overlay from the live item. */
  refreshHandles(): void;
  /** Optional: push a LIVE preview of the in-flight geometry into the canvas
   *  (#982). Targets whose visible rendering is derived from the MODEL rather
   *  than from the live item — a seam, whose guide and chisel marks are both
   *  re-derived — implement this so a drag shows up before release. Targets
   *  whose live item IS what's on screen (a part outline) need nothing. */
  previewLive?(): void;
  /** Optional: drop whatever `previewLive` pushed, on release or cancel. */
  clearPreview?(): void;
  /** Commit the final pathData under an undo label. `meta` carries the anchor
   *  locks + bezier memory remapped for the new anchor indices; targets that
   *  store those alongside their geometry write both in the SAME commit, so one
   *  undo takes the whole edit back. */
  commit(label: string, pathData: string, meta?: OutlineMetaPatch): void;
}

/** Anchor metadata travelling with a geometry commit. */
export interface OutlineMetaPatch {
  smoothAnchors?: ConstructionLine["smoothAnchors"];
  bezierMemory?: ConstructionLine["bezierMemory"];
}

export interface OutlineEditHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getSettings(): ToolSettings;
  getTarget(): OutlineTarget | null;
  /** Re-pick the hover cursor after a drag completes. */
  refreshEditCursor(p: { x: number; y: number }): void;
}

export class OutlineEditController {
  private anchorDrag: {
    targetKey: string;
    ref: OutlineAnchorRef;
  } | null = null;

  private handleDrag: {
    targetKey: string;
    ref: OutlineAnchorRef;
    side: "in" | "out";
    /** Lock inferred from the anchor's pre-drag geometry so a smooth
     *  anchor keeps mirroring through the whole drag even while the
     *  live handles are mid-mutation. */
    lockMode: "angle" | "smooth" | null;
  } | null = null;

  private curveDrag: {
    targetKey: string;
    ref: { pathIndex: number; curveIndex: number };
    snap: CurveBendSnapshot;
    /** Pre-drag lock modes of the bracketing anchors (see above). */
    seg1Lock: "angle" | "smooth" | null;
    seg2Lock: "angle" | "smooth" | null;
  } | null = null;

  constructor(private host: OutlineEditHost) {}

  hasActiveDrag(): boolean {
    return !!(this.anchorDrag || this.handleDrag || this.curveDrag);
  }

  reset(): void {
    this.anchorDrag = null;
    this.handleDrag = null;
    this.curveDrag = null;
    setAngleHint(null);
  }

  /** Distance-based handle hit-test on the target's LIVE item — same
   *  zoom-independent radii + tip-beats-anchor priority as the
   *  construction Edit mode, restricted to the single outline being
   *  edited. */
  findHandleHit(p: { x: number; y: number }): OutlineHandleHit | null {
    const target = this.host.getTarget();
    const item = target?.liveItem();
    if (!item) return null;
    const zoom = this.host.getScope().view?.zoom ?? 1;
    // A finger grabs them wider (#1150); the dots are drawn the same size.
    // Tips get no screen floor: they win over the anchor within range, so a
    // floor would let a short handle's tip steal the anchor's presses.
    const anchorR = dotHitRadiusMm(1.1 + 1 / zoom, zoom, currentPointerKind());
    const tipR = hitRadiusFor(0.7 + 0.25 / zoom, currentPointerKind());
    let bestTip: (OutlineHandleHit & { distance: number }) | null = null;
    let bestAnchor: (OutlineHandleHit & { distance: number }) | null = null;
    const subs = resolveSubpaths(item);
    for (let pathIndex = 0; pathIndex < subs.length; pathIndex++) {
      const sub = subs[pathIndex]!;
      if (!sub.segments) continue;
      for (
        let segmentIndex = 0;
        segmentIndex < sub.segments.length;
        segmentIndex++
      ) {
        const seg = sub.segments[segmentIndex]!;
        for (const side of ["in", "out"] as const) {
          const h = side === "in" ? seg.handleIn : seg.handleOut;
          if (h.isZero()) continue;
          const td = Math.hypot(
            seg.point.x + h.x - p.x,
            seg.point.y + h.y - p.y,
          );
          if (td > tipR) continue;
          if (!bestTip || td < bestTip.distance) {
            bestTip = {
              kind: "bezier-tip",
              pathIndex,
              segmentIndex,
              side,
              distance: td,
            };
          }
        }
        const ad = Math.hypot(seg.point.x - p.x, seg.point.y - p.y);
        if (ad > anchorR) continue;
        if (!bestAnchor || ad < bestAnchor.distance) {
          bestAnchor = {
            kind: "anchor",
            pathIndex,
            segmentIndex,
            distance: ad,
          };
        }
      }
    }
    if (bestTip) {
      const { distance: _d, ...hit } = bestTip;
      return hit;
    }
    if (bestAnchor) {
      const { distance: _d, ...hit } = bestAnchor;
      return hit;
    }
    return null;
  }

  /** Nearest point on the outline's stroke within grab range
   *  (`6/zoom` mm — gap-agnostic, same feel as construction). */
  findCurveHit(pt: paper.Point): OutlineCurveHit | null {
    const target = this.host.getTarget();
    const item = target?.liveItem();
    if (!item) return null;
    const zoom = this.host.getScope().view?.zoom ?? 1;
    const tolerance = hitRadiusFor(6, currentPointerKind()) / zoom;
    const subs = resolveSubpaths(item);
    let best: (OutlineCurveHit & { distance: number }) | null = null;
    for (let pathIndex = 0; pathIndex < subs.length; pathIndex++) {
      const sub = subs[pathIndex]!;
      const loc = sub.getNearestLocation(pt);
      if (!loc || !loc.curve) continue;
      const d = loc.point.getDistance(pt);
      if (d > tolerance) continue;
      if (!best || d < best.distance) {
        best = {
          pathIndex,
          curveIndex: loc.curve.index,
          t: loc.time ?? 0,
          distance: d,
        };
      }
    }
    if (!best) return null;
    const { distance: _d, ...hit } = best;
    return hit;
  }

  /** Seed a drag from an onDown hit. Priority mirrors construction:
   *  bezier tip → anchor → curve stroke. Returns true when the press
   *  was consumed. */
  maybeStartDrag(pt: paper.Point, handle: OutlineHandleHit | null): boolean {
    const target = this.host.getTarget();
    if (!target) return false;
    const item = target.liveItem();
    if (!item) return false;
    if (handle?.kind === "bezier-tip") {
      const seg = findSegmentAt(item, handle);
      const ref = {
        pathIndex: handle.pathIndex,
        segmentIndex: handle.segmentIndex,
      };
      this.handleDrag = {
        targetKey: target.key,
        ref,
        side: handle.side,
        lockMode: target.lockModeAt(ref),
      };
      return true;
    }
    if (handle?.kind === "anchor") {
      this.anchorDrag = {
        targetKey: target.key,
        ref: { pathIndex: handle.pathIndex, segmentIndex: handle.segmentIndex },
      };
      return true;
    }
    if (!target.allowCurves) return false;
    const curveHit = this.findCurveHit(pt);
    if (curveHit) {
      const curve = findCurveAt(item, curveHit);
      if (!curve) return false;
      this.curveDrag = {
        targetKey: target.key,
        ref: { pathIndex: curveHit.pathIndex, curveIndex: curveHit.curveIndex },
        snap: {
          t: curveHit.t,
          origHandleOut: {
            x: curve.segment1.handleOut.x,
            y: curve.segment1.handleOut.y,
          },
          origHandleIn: {
            x: curve.segment2.handleIn.x,
            y: curve.segment2.handleIn.y,
          },
          downPt: { x: pt.x, y: pt.y },
        },
        seg1Lock: target.lockModeAt({
          pathIndex: curveHit.pathIndex,
          segmentIndex: curve.segment1.index,
        }),
        seg2Lock: target.lockModeAt({
          pathIndex: curveHit.pathIndex,
          segmentIndex: curve.segment2.index,
        }),
      };
      setCanvasCursor(this.host.getScope(), "grabbing");
      return true;
    }
    return false;
  }

  /** Advance whichever drag is live. Returns true when consumed. */
  handleMove(s: PointerSample, p: { x: number; y: number }): boolean {
    const target = this.host.getTarget();
    if (this.anchorDrag) {
      if (!target || target.key !== this.anchorDrag.targetKey) {
        this.anchorDrag = null;
        return true;
      }
      const item = target.liveItem();
      const seg = item ? findSegmentAt(item, this.anchorDrag.ref) : null;
      if (seg) {
        const scope = this.host.getScope();
        const targetPos = this.snapDraggedAnchor(seg, p, angleModifierHeld(s));
        seg.point = new scope.Point(targetPos.x, targetPos.y);
        target.refreshHandles();
        target.previewLive?.();
      }
      return true;
    }
    if (this.handleDrag) {
      if (!target || target.key !== this.handleDrag.targetKey) {
        this.handleDrag = null;
        return true;
      }
      const item = target.liveItem();
      const seg = item ? findSegmentAt(item, this.handleDrag.ref) : null;
      if (seg) {
        const scope = this.host.getScope();
        const offset = new scope.Point(p.x - seg.point.x, p.y - seg.point.y);
        if (this.handleDrag.side === "in") seg.handleIn = offset;
        else seg.handleOut = offset;
        const mirrored = mirroredHandleFor(
          scope,
          seg,
          this.handleDrag.side,
          this.handleDrag.lockMode,
        );
        if (mirrored) {
          if (this.handleDrag.side === "in") seg.handleOut = mirrored;
          else seg.handleIn = mirrored;
        }
        target.refreshHandles();
        target.previewLive?.();
      }
      return true;
    }
    if (this.curveDrag) {
      if (!target || target.key !== this.curveDrag.targetKey) {
        this.curveDrag = null;
        return true;
      }
      const item = target.liveItem();
      const curve = item ? findCurveAt(item, this.curveDrag.ref) : null;
      if (curve) {
        const scope = this.host.getScope();
        applyCurveBend(scope, curve, this.curveDrag.snap, p);
        // Locked bracketing anchors keep their OUTSIDE tangent
        // consistent with the lock while the curve bends.
        const m1 = mirroredHandleFor(
          scope,
          curve.segment1,
          "out",
          this.curveDrag.seg1Lock,
        );
        if (m1) curve.segment1.handleIn = m1;
        const m2 = mirroredHandleFor(
          scope,
          curve.segment2,
          "in",
          this.curveDrag.seg2Lock,
        );
        if (m2) curve.segment2.handleOut = m2;
        target.refreshHandles();
        target.previewLive?.();
      }
      return true;
    }
    return false;
  }

  /** Commit whichever drag is live and clear it. Returns true when
   *  the release was consumed. Commits read the live (already
   *  mutated) item's pathData — the same commit-what's-rendered
   *  trick the construction drags use. */
  handleUp(p: { x: number; y: number }): boolean {
    const target = this.host.getTarget();
    if (this.anchorDrag) {
      const drag = this.anchorDrag;
      this.anchorDrag = null;
      setAngleHint(null);
      if (target && target.key === drag.targetKey) {
        const item = target.liveItem();
        const d = item?.pathData;
        // Drop the live preview BEFORE committing: the commit re-renders from
        // the model, and a stale override would paint the in-flight geometry
        // over it (#982).
        target.clearPreview?.();
        if (d) target.commit(`Edit ${target.noun}`, d);
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    if (this.handleDrag) {
      const drag = this.handleDrag;
      this.handleDrag = null;
      if (target && target.key === drag.targetKey) {
        const item = target.liveItem();
        const d = item?.pathData;
        // Drop the live preview BEFORE committing: the commit re-renders from
        // the model, and a stale override would paint the in-flight geometry
        // over it (#982).
        target.clearPreview?.();
        if (d) target.commit(`Adjust ${target.noun} handle`, d);
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    if (this.curveDrag) {
      const drag = this.curveDrag;
      this.curveDrag = null;
      if (target && target.key === drag.targetKey) {
        const item = target.liveItem();
        const d = item?.pathData;
        // Drop the live preview BEFORE committing: the commit re-renders from
        // the model, and a stale override would paint the in-flight geometry
        // over it (#982).
        target.clearPreview?.();
        if (d) target.commit(`Bend ${target.noun}`, d);
      }
      this.host.refreshEditCursor(p);
      return true;
    }
    return false;
  }

  /** Grid / Angle snap for the dragged anchor — the same modifier
   *  precedence as construction's edit mode (#298) minus the
   *  construction-only line-snap tier: angle wins (measured from the
   *  neighbouring anchor), grid is the fallback. */
  private snapDraggedAnchor(
    seg: paper.Segment,
    p: { x: number; y: number },
    shiftKey: boolean,
  ): { x: number; y: number } {
    const neighborSeg = seg.previous ?? seg.next;
    const neighbor = neighborSeg
      ? { x: neighborSeg.point.x, y: neighborSeg.point.y }
      : null;
    if (neighbor && isAngleSnapActive(this.host.getSettings(), shiftKey)) {
      const sn = snapToCommonAngle(neighbor, p);
      setAngleHint(sn.angleRad);
      return { x: sn.x, y: sn.y };
    }
    setAngleHint(null);
    if (isGridSnapActive(this.host.getSettings())) {
      const grid = this.host.getScene().getMatGridInfo();
      if (grid) return snapPointToGrid(p, grid);
    }
    return { x: p.x, y: p.y };
  }
}
